// Pago en el navegador con Culqi (Checkout v4 + 3DS), Izipay (formulario oficial)
// o Mercado Pago (SDK oficial): tarjeta con su formulario seguro y Yape con el
// código de aprobación.
// El importe, la orden y el token los prepara el servidor; aquí solo se abre el
// formulario del proveedor y se envía su resultado al servidor, que lo verifica
// (Culqi: cargo con la llave secreta; Izipay: firma HMAC). Nada se da por pagado
// en el navegador. Los scripts solo se cargan de los dominios oficiales.
import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, Lock, Smartphone } from 'lucide-react';
import { operarOrden } from '../../../servicios/ordenServicio';
import { solesDeCentimos } from '../../../dominio/logicaTiendaPublica';
import { simboloMoneda } from '../../../dominio/moneda';

const CULQI_CHECKOUT = 'https://checkout.culqi.com/js/v4';
const CULQI_3DS = 'https://3ds.culqi.com';
const SDK_IZIPAY = ['https://sandbox-checkout.izipay.pe/payments/v1/js/index.js', 'https://checkout.izipay.pe/payments/v1/js/index.js'];

const cargados = new Map();
/** Carga un script de proveedor una sola vez por página. */
export const cargarScript = (src) => {
  if (!cargados.has(src)) {
    cargados.set(src, new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src; script.async = true;
      script.onload = () => resolve();
      script.onerror = () => { cargados.delete(src); reject(new Error('No se pudo abrir el formulario de pago. Revisa tu conexión e inténtalo de nuevo.')); };
      document.head.appendChild(script);
    }));
  }
  return cargados.get(src);
};

const mensajeError = (e) => (e?.message && !/^internal$/i.test(e.message) ? e.message : 'No se pudo completar el pago. Consulta el estado antes de intentar de nuevo.');

const BotonPagar = ({ ocupado, texto, onClick }) => <button type="button" className="tp-boton tp-boton--primario tp-boton--ancho" disabled={ocupado} onClick={onClick}>
  <CreditCard size={16} aria-hidden="true" />{texto}</button>;

// ── Culqi ─────────────────────────────────────────────────────────────────────
export const PagoCulqi = ({ gymId, resultado, nombreTienda, onActualizado }) => {
  const datos = resultado.pasarela?.culqi;
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');
  const dispositivo = useRef('');
  const pendiente3DS = useRef(null); // { tokenId, email } mientras el banco autentica

  const cobrar = async ({ tokenId, email, parameters3DS }) => {
    setOcupado(true); setAviso('');
    try {
      const respuesta = await operarOrden({ accion: 'culqi_cobrar_publico', gymId, idempotencyKey: resultado.claveConsulta,
        tokenId, email, deviceId: dispositivo.current, ...(parameters3DS ? { parameters3DS } : {}) });
      if (respuesta.requiere3ds) {
        pendiente3DS.current = { tokenId, email };
        window.Culqi3DS.settings = { charge: { totalAmount: datos.montoCentimos, returnUrl: window.location.href }, card: { email } };
        window.Culqi3DS.initAuthentication(tokenId);
        return;
      }
      if (respuesta.rechazado) setAviso(respuesta.mensaje || 'El pago no fue aprobado. Intenta con otro medio.');
      onActualizado(respuesta);
    } catch (e) { setAviso(mensajeError(e)); }
    finally { setOcupado(false); }
  };

  // Resultado de la autenticación 3DS: el script de Culqi lo publica en esta misma ventana.
  useEffect(() => {
    const alRecibir = (evento) => {
      if (evento.origin !== window.location.origin || !pendiente3DS.current) return;
      const { parameters3DS, error } = evento.data || {};
      if (parameters3DS) { const datos3DS = pendiente3DS.current; pendiente3DS.current = null; cobrar({ ...datos3DS, parameters3DS }); }
      if (error) { pendiente3DS.current = null; setAviso('Tu banco no autenticó el pago. Intenta de nuevo o usa otro medio.'); }
      window.Culqi3DS?.reset?.();
    };
    window.addEventListener('message', alRecibir);
    return () => window.removeEventListener('message', alRecibir);
  });

  const abrir = async () => {
    setOcupado(true); setAviso('');
    try {
      await Promise.all([cargarScript(CULQI_CHECKOUT), cargarScript(CULQI_3DS)]);
      const { Culqi, Culqi3DS } = window;
      Culqi.publicKey = datos.publicKey;
      Culqi.settings({ title: String(nombreTienda || 'Tienda').slice(0, 40), currency: 'PEN', amount: datos.montoCentimos,
        order: datos.orderId, description: String(datos.descripcion || 'Pedido').slice(0, 80) });
      Culqi.options({ lang: 'auto', installments: false,
        paymentMethods: { tarjeta: true, yape: true, billetera: true, bancaMovil: true, agente: true, cuotealo: false } });
      if (Culqi3DS) {
        Culqi3DS.publicKey = datos.publicKey;
        Culqi3DS.options = { showModal: true, showLoading: true, showIcon: true, closeModalAction: () => setOcupado(false) };
        try { dispositivo.current = (await Culqi3DS.generateDevice()) || ''; } catch { dispositivo.current = ''; }
      }
      window.culqi = () => {
        if (window.Culqi.token) {
          const { id, email } = window.Culqi.token;
          window.Culqi.close();
          cobrar({ tokenId: id, email });
        } else if (window.Culqi.order) {
          // Yape por orden, billetera, banca móvil o agente: Culqi confirma después.
          window.Culqi.close();
          setAviso('Recibimos tu solicitud de pago. Cuando Culqi lo confirme, tu pedido pasará a «Preparando». Consulta el estado en unos minutos.');
        } else if (window.Culqi.error) {
          setAviso(window.Culqi.error.user_message || 'No se pudo completar el pago.');
        }
      };
      Culqi.open();
    } catch (e) { setAviso(mensajeError(e)); }
    finally { setOcupado(false); }
  };

  if (!datos?.publicKey || !datos?.orderId) return null;
  return <>
    <BotonPagar ocupado={ocupado} texto={`Pagar ${solesDeCentimos(datos.montoCentimos)}`} onClick={abrir} />
    {aviso && <p className="tp-nota" role="status">{aviso}</p>}
  </>;
};

// ── Yape con Mercado Pago (Checkout API) ──────────────────────────────────────
// El SDK oficial convierte el celular y el código de aprobación de la app Yape en
// un token de un solo uso (con la Public Key del gym). El servidor cobra con ese
// token y responde al instante: aprobado (pasa a «Preparando») o rechazado.
const SDK_MERCADO_PAGO = 'https://sdk.mercadopago.com/js/v2';
const CORREO = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

// cobrar({ token, email }): igual que en la tarjeta. Devuelve la vista con rechazado/mensaje.
export const PagoYape = ({ gymId, resultado, prueba, onActualizado, titulo = true, cobrar = null }) => {
  const publicKey = resultado.pasarela?.yape?.publicKey;
  const [datos, setDatos] = useState({ celular: '', codigo: '', email: '' });
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');
  const cambiar = (campo, valor) => setDatos((d) => ({ ...d, [campo]: valor }));
  const valido = /^9\d{8}$/.test(datos.celular) && /^\d{6}$/.test(datos.codigo) && CORREO.test(datos.email.trim());

  const pagar = async (evento) => {
    evento.preventDefault();
    if (!valido || ocupado) return;
    setOcupado(true); setAviso('');
    let token = '';
    try {
      await cargarScript(SDK_MERCADO_PAGO);
      const mp = new window.MercadoPago(publicKey, { locale: 'es-PE' });
      token = (await mp.yape({ otp: datos.codigo, phoneNumber: datos.celular }).create())?.id || '';
      if (!token) throw new Error('sin token');
    } catch {
      // El código es de un solo uso: se pide uno nuevo en cada intento.
      cambiar('codigo', '');
      setAviso('No se pudo validar tu Yape. Revisa el celular, genera un código nuevo en tu app e inténtalo otra vez.');
      setOcupado(false);
      return;
    }
    try {
      const vista = await (cobrar ? cobrar({ token, email: datos.email.trim() })
        : operarOrden({ accion: 'mp_yape_pagar_publico', gymId, idempotencyKey: resultado.claveConsulta, token, email: datos.email.trim() }));
      if (vista.rechazado) setAviso(vista.mensaje || 'Yape no aprobó el pago. Genera un código nuevo e inténtalo otra vez.');
      onActualizado(vista);
    } catch (e) { setAviso(mensajeError(e)); }
    finally { cambiar('codigo', ''); setOcupado(false); }
  };

  if (!publicKey || !resultado.claveConsulta) return null;
  return <form className="tp-pago-yape" onSubmit={pagar} noValidate aria-label={titulo ? undefined : 'Pagar con Yape'} aria-labelledby={titulo ? 'pago-yape-titulo' : undefined}>
    {titulo && <h3 id="pago-yape-titulo">Paga con Yape</h3>}
    <p>En tu app Yape busca «Código de aprobación» y escribe aquí los 6 dígitos. El código vence en unos minutos.</p>
    {prueba && <p>Modo de pruebas: usa el celular 111111111 y el código 123456.</p>}
    <div className="tp-campos-dobles">
      <label className="tp-campo"><span>Celular de Yape</span>
        <input inputMode="numeric" autoComplete="tel-national" maxLength={9} placeholder="987654321" value={datos.celular} disabled={ocupado}
          onChange={(e) => cambiar('celular', e.target.value.replace(/\D/g, ''))} /></label>
      <label className="tp-campo"><span>Código de aprobación</span>
        <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6 dígitos" value={datos.codigo} disabled={ocupado}
          onChange={(e) => cambiar('codigo', e.target.value.replace(/\D/g, ''))} /></label>
    </div>
    <label className="tp-campo"><span>Correo (para tu comprobante)</span>
      <input type="email" autoComplete="email" maxLength={254} value={datos.email} disabled={ocupado} onChange={(e) => cambiar('email', e.target.value)} /></label>
    <button type="submit" className="tp-boton tp-boton--primario tp-boton--ancho" disabled={!valido || ocupado}>
      <Smartphone size={16} aria-hidden="true" />{ocupado ? 'Procesando pago…' : `Pagar ${solesDeCentimos(resultado.totalCentimos)} con Yape`}</button>
    {aviso && <p className="tp-nota" role="status">{aviso}</p>}
  </form>;
};

// ── Tarjeta con Mercado Pago (Checkout API, Card Payment Brick) ───────────────
// El formulario seguro de Mercado Pago se dibuja aquí mismo: número, fecha y CVV
// se escriben en sus campos (iframes de Mercado Pago) y a AllGym solo llega el
// token de un solo uso. El servidor cobra con el Access Token del gym y responde
// al instante. Sin pestañas nuevas ni redirecciones.
const TARJETA_PRUEBA = 'Modo de pruebas: usa la tarjeta 5031 7557 3453 0604, vencimiento 11/30, CVV 123 y como titular APRO.';

// El botón del formulario toma el color de la tienda (solo si es un hex que Mercado Pago entienda).
const colorMarca = (elemento) => {
  const valor = elemento ? window.getComputedStyle(elemento).getPropertyValue('--tp-marca').trim() : '';
  return /^#[0-9a-f]{6}$/i.test(valor) ? valor : '';
};

// cobrar({ token, metodo, emisor, email, documento }): caso de uso que cobra (por
// defecto, el pedido de la tienda; la mensualidad en línea pasa el suyo).
export const PagoTarjeta = ({ gymId, resultado, prueba, onActualizado, enlaceAlterno = '', cobrar = null }) => {
  const publicKey = resultado.pasarela?.tarjeta?.publicKey || resultado.pasarela?.yape?.publicKey;
  const totalCentimos = resultado.totalCentimos;
  const idContenedor = `tp-tarjeta-${String(resultado.claveConsulta || '').slice(0, 8)}`;
  const contenedor = useRef(null);
  const [estado, setEstado] = useState('cargando');
  const [aviso, setAviso] = useState('');
  // El formulario se crea una vez; sus callbacks leen siempre los datos vigentes.
  const vigente = useRef({});
  vigente.current = { gymId, clave: resultado.claveConsulta, onActualizado, cobrar };

  useEffect(() => {
    if (!publicKey) return undefined;
    let activo = true, controlador = null;
    // Mercado Pago espera la promesa: resuelta, el pago terminó; rechazada, el
    // comprador corrige y reintenta (cada envío genera un token nuevo).
    const pagar = async (datos) => {
      const { gymId: gym, clave, onActualizado: actualizar, cobrar: cobrarCaso } = vigente.current;
      const documento = datos?.payer?.identification;
      setAviso('');
      let vista;
      try {
        const cobro = { token: datos?.token, metodo: datos?.payment_method_id, emisor: datos?.issuer_id ? String(datos.issuer_id) : '',
          email: datos?.payer?.email || '', ...(documento?.number ? { documento: { tipo: documento.type, numero: documento.number } } : {}) };
        vista = await (cobrarCaso ? cobrarCaso(cobro) : operarOrden({ accion: 'mp_tarjeta_pagar_publico', gymId: gym, idempotencyKey: clave, ...cobro }));
      } catch (e) { setAviso(mensajeError(e)); throw e; }
      if (vista.rechazado) setAviso(vista.mensaje || 'Tu banco no aprobó el pago. Usa otra tarjeta o paga con Yape.');
      actualizar(vista);
      if (vista.rechazado) throw new Error('rechazado');
    };
    (async () => {
      try {
        await cargarScript(SDK_MERCADO_PAGO);
        if (!activo) return;
        const marca = colorMarca(contenedor.current);
        const mp = new window.MercadoPago(publicKey, { locale: 'es-PE' });
        const creado = await mp.bricks().create('cardPayment', idContenedor, {
          initialization: { amount: totalCentimos / 100 },
          customization: {
            paymentMethods: { maxInstallments: 1 },
            visual: { hideFormTitle: true, texts: { formSubmit: `Pagar ${solesDeCentimos(totalCentimos)}` },
              style: { theme: 'default', customVariables: { ...(marca ? { baseColor: marca } : {}), borderRadiusMedium: '8px', borderRadiusLarge: '12px', formPadding: '0px' } } },
          },
          callbacks: {
            onReady: () => { if (activo) setEstado('listo'); },
            onSubmit: (datos) => pagar(datos),
            onError: (error) => { if (activo && error?.type === 'critical') setEstado('error'); },
          },
        });
        if (activo) controlador = creado; else creado?.unmount?.();
      } catch { if (activo) setEstado('error'); }
    })();
    return () => { activo = false; try { controlador?.unmount?.(); } catch { /* ya desmontado */ } };
  }, [publicKey, idContenedor, totalCentimos]);

  if (!publicKey || !resultado.claveConsulta) return null;
  return <div className="tp-pago-tarjeta">
    {prueba && <p className="tp-nota">{TARJETA_PRUEBA}</p>}
    {estado === 'cargando' && <p className="tp-nota" role="status">Cargando el formulario seguro de Mercado Pago…</p>}
    <div id={idContenedor} ref={contenedor} className="tp-brick" hidden={estado === 'error'} />
    {estado === 'error' && <p className="tp-nota" role="alert">No se pudo abrir el formulario de tarjeta.
      {enlaceAlterno ? ' Puedes pagar en la página segura de Mercado Pago.' : ' Recarga la página o paga con Yape.'}</p>}
    {estado === 'error' && enlaceAlterno && <a className="tp-boton tp-boton--secundario tp-boton--ancho" href={enlaceAlterno}>
      <CreditCard size={16} aria-hidden="true" />Pagar en Mercado Pago</a>}
    {aviso && <p className="tp-error" role="alert">{aviso}</p>}
    <p className="tp-pago-seguro"><Lock size={14} aria-hidden="true" />
      <span>Los datos de tu tarjeta van cifrados directo a Mercado Pago: ni el gimnasio ni AllGym los ven.</span></p>
  </div>;
};

// ── Izipay ────────────────────────────────────────────────────────────────────
export const PagoIzipay = ({ gymId, resultado, onActualizado }) => {
  const datos = resultado.pasarela?.izipay;
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');

  const confirmar = async (respuesta) => {
    // Sin comunicación con Izipay no hay firma: el resultado se conoce por su IPN.
    if (!respuesta?.payloadHttp || !respuesta?.signature || ['021', 'COMMUNICATION_ERROR'].includes(respuesta?.code)) {
      setAviso('No se confirmó la respuesta de Izipay. No vuelvas a pagar: consulta el estado del pedido en unos minutos.');
      return;
    }
    setOcupado(true);
    try {
      const vista = await operarOrden({ accion: 'izipay_confirmar_publico', gymId, idempotencyKey: resultado.claveConsulta,
        payloadHttp: respuesta.payloadHttp, signature: respuesta.signature });
      if (vista.rechazado) setAviso(vista.mensaje || 'El pago no fue aprobado. Intenta con otro medio.');
      onActualizado(vista);
    } catch (e) { setAviso(mensajeError(e)); }
    finally { setOcupado(false); }
  };

  const abrir = async () => {
    if (!SDK_IZIPAY.includes(datos.sdkUrl)) { setAviso('Formulario de pago no disponible.'); return; }
    setOcupado(true); setAviso('');
    try {
      await cargarScript(datos.sdkUrl);
      const Izipay = window.Izipay;
      const checkout = new Izipay({ config: {
        transactionId: datos.transactionId, action: Izipay.enums?.payActions?.PAY || 'pay', merchantCode: datos.merchantCode,
        order: { orderNumber: datos.orderNumber, currency: datos.currency, amount: datos.amount,
          processType: Izipay.enums?.processType?.AUTHORIZATION || 'AT', merchantBuyerId: datos.merchantBuyerId, dateTimeTransaction: datos.dateTimeTransaction },
        render: { typeForm: Izipay.enums?.typeForm?.POP_UP || 'pop-up' },
        urlIPN: datos.urlIPN,
      } });
      checkout.LoadForm({ authorization: datos.token, keyRSA: datos.publicKeyRsa, callbackResponse: confirmar });
    } catch (e) { setAviso(e?.message ? mensajeError(e) : 'No se pudo abrir el formulario de Izipay.'); }
    finally { setOcupado(false); }
  };

  if (!datos?.token) return null;
  return <>
    <BotonPagar ocupado={ocupado} texto={`Pagar ${simboloMoneda()} ${datos.amount}`} onClick={abrir} />
    {aviso && <p className="tp-nota" role="status">{aviso}</p>}
  </>;
};
